/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   This is the standard text input component
 * use correct autocompletes from https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
 */

import { Tooltip } from '@chakra-ui/react'
import { Clear, ErrorIcon } from '@zcode-ui/icons'
import { Dots } from '@zcode-ui/loaders'
import { isPadMapper as _isPadMapper } from '@zcode/env'
import React, { useCallback, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'

interface BaseTextInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  classNameForInnerWrapper?: string
  classNameForMiddleWrapper?: string
  customErrorCallback?: (
    value: string | undefined,
    type: React.HTMLInputTypeAttribute
  ) => Errors[] | []
  descriptionText?: string
  errors?: Errors[] | Errors | string
  hideContainerCss?: boolean
  inputOverlay?: React.ReactNode
  isErrorWithIcon?: boolean
  isPadMapper?: boolean
  label?: string
  loading?: boolean
  placeholder?: string
  useNativeValidation?: boolean
  value?: string
  width?: string
}

interface ClearInputProps extends BaseTextInputProps {
  onChange?: (
    val:
      | React.ClipboardEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLInputElement>
      | { currentTarget?: never; target: { value: '' } }
  ) => void
  showClearInput: true
}
interface UnclearInputProps extends BaseTextInputProps {
  onChange?: (
    val:
      | React.ClipboardEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLInputElement>
  ) => void
  showClearInput?: false
}

export type TextInputProps = ClearInputProps | UnclearInputProps

interface Errors {
  message?: string
  type?: string
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(
    {
      autoComplete = 'on',
      className,
      classNameForMiddleWrapper,
      classNameForInnerWrapper,
      customErrorCallback,
      descriptionText,
      disabled = false,
      errors: propErrors = [],
      hideContainerCss,
      id,
      inputOverlay,
      isErrorWithIcon,
      isPadMapper = _isPadMapper,
      label = '',
      loading = false,
      maxLength = 255,
      onBlur,
      onChange,
      onFocus,
      onInput,
      onInvalid,
      placeholder,
      required = false,
      showClearInput,
      type = 'text',
      useNativeValidation,
      value,
      width = '281px',
      ...forwardProps
    }: TextInputProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) {
    const [errors, setErrors] = useState<Errors[]>([])
    const [tooltipIsOpen, setTooltipIsOpen] = useState<true | undefined>(
      forwardProps.autoFocus || undefined
    )

    const errorArray = useMemo(() => {
      if (typeof propErrors === 'string') {
        return [{ message: propErrors }]
      }
      if (!Array.isArray(propErrors)) {
        return [propErrors]
      }
      return propErrors
    }, [propErrors])
    const clearInput = useCallback(() => {
      if (!showClearInput) {
        // this lets TypeScript know that we have the right kind of onChange
        return
      }
      setErrors([])
      onChange?.({ target: { value: '' } })
    }, [onChange, showClearInput])

    const handleChange = useMemo(
      () =>
        useNativeValidation
          ? onChange
          : (
              event:
                | React.ClipboardEvent<HTMLInputElement>
                | React.ChangeEvent<HTMLInputElement>
            ) => {
              setErrors([])
              onChange?.(event)
            },
      [onChange, useNativeValidation]
    )
    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (useNativeValidation) {
          e.currentTarget.reportValidity()
        } else {
          const errors = customErrorCallback
            ? customErrorCallback(value, type)
            : []
          setErrors(errors)
        }
        setTooltipIsOpen(undefined)
        onBlur?.(e)
      },
      [onBlur, customErrorCallback, type, useNativeValidation, value]
    )
    const handleFocus = useMemo(
      () =>
        useNativeValidation
          ? (e: React.FocusEvent<HTMLInputElement>) => {
              setTooltipIsOpen(true)
              onFocus?.(e)
            }
          : onFocus,
      [onFocus, useNativeValidation]
    )
    const handleInput = useMemo(
      () =>
        useNativeValidation
          ? (e: React.FormEvent<HTMLInputElement>) => {
              const { currentTarget } = e
              if (customErrorCallback) {
                currentTarget.setCustomValidity(
                  customErrorCallback(
                    currentTarget.value,
                    currentTarget.type
                  )[0]?.message ?? ''
                )
              }
              setErrors((errors) => {
                if (currentTarget.validity.valid) {
                  return []
                }
                if (
                  errors.length &&
                  errors[0].message !== currentTarget.validationMessage
                ) {
                  return [{ message: currentTarget.validationMessage }]
                }
                return errors
              })
              onInput?.(e)
            }
          : onInput,
      [customErrorCallback, onInput, useNativeValidation]
    )
    const handleInvalid = useMemo(
      () =>
        useNativeValidation
          ? (e: React.FormEvent<HTMLInputElement>) => {
              e.preventDefault()
              setErrors([{ message: e.currentTarget.validationMessage }])
              onInvalid?.(e)
            }
          : onInvalid,
      [onInvalid, useNativeValidation]
    )

    const allErrors = useMemo(
      () => [...errorArray, ...errors],
      [errorArray, errors]
    )
    const errorComponents = useMemo(
      () =>
        allErrors.map((e) => {
          return (
            <div
              className={`first-letter:uppercase font-body leading-[normal] lowercase ${
                isPadMapper ? 'mt-1' : 'mt-2'
              } text-red-500 text-xs`}
              key={e.message}
            >
              {e.message}
            </div>
          )
        }),
      [allErrors, isPadMapper]
    )

    const hasErrors = Boolean(allErrors.length)
    const showErrorIcon = isErrorWithIcon && allErrors.length
    return (
      <div
        className={
          hideContainerCss || isPadMapper
            ? undefined
            : 'font-body relative rounded-md'
        }
        style={hideContainerCss || isPadMapper ? undefined : { width }}
      >
        <div
          className={twMerge(
            `border ${
              hasErrors ? 'border-red-500' : 'border-gray-150'
            } border-solid overflow-hidden ${
              isPadMapper
                ? 'rounded-[3px] focus-within:border-blue-300'
                : 'rounded-md focus-within:border-neutral-500 focus-within:shadow-[0_0_0_4px_var(--chakra-colors-neutral-200)]'
            }`,
            classNameForMiddleWrapper
          )}
        >
          <div
            className={twMerge(
              `bg-gray-0 flex h-full relative ${
                isPadMapper ? 'rounded-[2px]' : 'rounded-[5px]'
              }`,
              classNameForInnerWrapper
            )}
          >
            <input
              aria-invalid={hasErrors}
              aria-label={label}
              aria-required={useNativeValidation ? undefined : required}
              autoComplete={autoComplete}
              className={twMerge(
                isPadMapper && 'no-default-styles', // this can be deleted once we remove mentions of no-default-styles from web-padmapper
                'bg-transparent border-0 focus:border-0 focus:outline-none focus:shadow-none leading-[21px] outline-none overflow-visible peer',
                'placeholder:text-gray-300',
                'placeholder:opacity-0 placeholder:invisible focus:placeholder:opacity-100 focus:placeholder:visible not-placeholder-shown:placeholder:opacity-100 not-placeholder-shown:placeholder:visible',
                `${
                  isPadMapper
                    ? label
                      ? 'pl-2.5 pt-4 pb-1'
                      : 'pl-4 pt-2.5 pb-2.5'
                    : label
                      ? 'pt-6 pb-[9px]'
                      : 'pt-4 pb-[17px]'
                } ${
                  showClearInput || inputOverlay
                    ? 'rounded-r-none pr-0 w-[calc(100%-50px)]'
                    : `${isPadMapper ? 'pl-2.5' : 'pr-4'} w-full`
                } ${
                  isPadMapper
                    ? 'placeholder:text-[16px] text-[16px]'
                    : 'font-body pl-4 placeholder:text-[1rem] rounded-[5px] text-[1rem]'
                } z-[1]`,
                className
              )}
              disabled={disabled || loading}
              id={id}
              maxLength={maxLength}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={handleFocus}
              onInput={handleInput}
              onInvalid={handleInvalid}
              onPaste={handleChange}
              placeholder={(loading ? undefined : placeholder) || ' '}
              ref={ref}
              required={useNativeValidation ? required : undefined}
              type={type}
              value={value}
              {...forwardProps}
            />
            {label && !loading && (
              <span
                className={`absolute cursor-text duration-100 ease-in-out font-body font-normal ${
                  isPadMapper ? 'ml-2.5' : 'ml-4'
                } origin-top-left pointer-events-none scale-[0.75] ${
                  isPadMapper ? 'translate-y-[4px]' : 'translate-y-[8px]'
                } peer-placeholder-shown:scale-[1] ${
                  isPadMapper
                    ? 'peer-placeholder-shown:translate-y-[10px]'
                    : 'peer-placeholder-shown:translate-y-[14px]'
                } peer-focus:peer-placeholder-shown:scale-[0.75] ${
                  isPadMapper
                    ? 'peer-focus:peer-placeholder-shown:translate-y-[4px] text-[16px]'
                    : 'peer-focus:peer-placeholder-shown:translate-y-[8px] text-[1rem]'
                } text-gray-600 transition-all z-[1]`}
              >{`${label}${required ? '*' : ''}`}</span>
            )}
            {loading && (
              <div className="absolute flex h-[calc(100%-2px)] items-center left-6">
                <Dots />
              </div>
            )}
            {(inputOverlay ?? showClearInput) && (
              <div
                className={`absolute bg-gray-0 border-0 cursor-text flex font-body ${
                  isPadMapper ? '' : 'h-[54px]'
                } items-center justify-center right-0 rounded-[5px] rounded-l-none text-gray-600 top-0 w-[50px]`}
              >
                {showClearInput && value && (
                  <button
                    aria-label="Clear Input"
                    className="bg-gray-0 border-0 font-body text-gray-600"
                    onClick={clearInput}
                    type="button"
                  >
                    <Clear />
                  </button>
                )}
                {inputOverlay}
              </div>
            )}
            {showErrorIcon ? (
              <div
                className={`flex ${
                  isPadMapper ? '' : 'h-[54px]'
                } items-center justify-center relative w-[50px]`}
              >
                <Tooltip
                  fontSize="xs"
                  hasArrow
                  isOpen={tooltipIsOpen}
                  label={allErrors[0].message}
                  placement="top-end"
                >
                  <ErrorIcon
                    alt={['Error', allErrors[0]?.message]
                      .filter(Boolean)
                      .join(': ')}
                  />
                </Tooltip>
              </div>
            ) : null}
          </div>
        </div>

        {descriptionText && !hasErrors && (
          <div
            className={`font-body font-normal ${
              isPadMapper ? 'mt-1' : 'mt-2'
            } text-gray-800 text-xs`}
          >
            {descriptionText}
          </div>
        )}

        {!isErrorWithIcon ? errorComponents : null}
      </div>
    )
  }
)
